import _ from 'underscore';
import ko from 'knockout';

import { Selector } from '@bingads-webui/selector';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';

import layoutTemplate from './template/value/selector-predicate.pug';
import { Predicate } from './predicate';
import { withKeyMap } from './i18n/extensions';

/*
 * This is a customized view for selector in filter.
 * It triggers 'change:selection' when user updates selection, with {selectedIds}.
 * Consumer can use getSelectedNames to get the selected names.
 */
export class SelectorPredicate extends Predicate {
  get className() {
    return 'selector-predicate';
  }

  initialize(options) {
    super.initialize(options);
    this.selector = null;
    this.selectorRendered = false;
    this.i18n = options.i18n;
    this.ko_model.i18n = options.i18n;
    this.ko_model.selectedIds = ko.observable(this.ko_model.values() || []);
    this.ko_model.selectedIds.subscribe((newValues) => {
      this.ko_model.value(newValues);
      this.ko_model.values(newValues);
    }, this);
    this.dataSource = this.ko_model.metadata().dataSource;
    this.templates = this.ko_model.metadata().templates;
  }

  render() {
    this.prepareView();
    return this;
  }

  prepareView() {
    const layout = layoutTemplate({
      i18n: this.i18n,
      name: this.ko_model.metadata().title || this.ko_model.metadata().name,
    });

    this.$el.html(layout);
    ko.applyBindings(this.ko_model, this.el);

    this.initSelector();
  }

  initSelector() {
    if (this.selector) {
      return;
    }

    const checkMap = this.generateCheckMap();
    const configSelectorOptionsCopy = cloneDeep(this.ko_model.metadata().selectorOptions) || {};
    const configSelectorOptionsOriginal = this.ko_model.metadata().selectorOptions || {};

    const keyMap = _.extend({}, {
      Selector_NoDataMessage: () => this.i18n.getString('NoEntityOrEntities').replace('{0}', this.ko_model.metadata().name),
      Selector_MoreThanLimit: () => this.i18n.getString('Filter_Selector_MoreThanLimit')
        .replace('{0}', this.dataSource.itemsDisplayLimit).replace(/\{1\}/g, this.ko_model.metadata().name),
      Selector_Filter_Selected: (unused, key, params) => this.i18n.getString(key, params),
    }, configSelectorOptionsOriginal.keyMap);

    const selectorI18n = withKeyMap(this.i18n, keyMap);

    if (configSelectorOptionsCopy.handleFilterChange &&
      !_.isFunction(configSelectorOptionsCopy.handleFilterChange)) {
      // clone deep doesn't properly handle functions
      configSelectorOptionsCopy.handleFilterChange =
        configSelectorOptionsOriginal.handleFilterChange;
    }

    configSelectorOptionsCopy.styles = _.defaults(configSelectorOptionsCopy.styles || {}, {
      searchIcon: ['glyphicon', 'filter_search_icon_small'],
      list: ['table--fixed-layout', 'table--no-border', 'table--no-header'],
      checkboxColumn: ['table__cell--menu-item-style', 'table__cell--icon'],
      nameColumn: ['table__cell--menu-item-style', 'table__cell--ellipsis-overflow'],
    });
    const selectorOptions = _.defaults(configSelectorOptionsCopy, {
      busyIndicator: {
        enabled: true,
      },
      data: {
        checkMap,
        itemsDisplayLimit: this.dataSource.itemsDisplayLimit,
      },
      filter: {
        showSelected: false,
        showChanged: false,
        disableSearch: this.dataSource.disableSearch,
      },
      i18n: selectorI18n,
      showNoDataMessage: true,
      templates: this.templates,
    });

    // prepare for selector view
    this.selector = new Selector(selectorOptions);

    this.selector.on('request:data', ({ viewFilter, searchFilter, stats }) => {
      if (_.isFunction(selectorOptions.handleFilterChange)) {
        selectorOptions.handleFilterChange({
          viewFilter,
          searchFilter,
          stats,
          dataSource: this.dataSource,
          selector: this.selector,
        });
      } else {
        this.selector.setData(this.dataSource.get(viewFilter, searchFilter, stats));
      }
    });

    // FIXME: stored filter sanitization will not work like before

    this.selector.on('change:selection', (stats) => {
      const selectedIds = _.pluck(stats.checked, 'id');

      this.ko_model.selectedIds(selectedIds);
      this.trigger('change:selection', {
        selectedIds,
      });
    });

    // render selector
    this.$('.selector-placeholder').append(this.selector.el);
    this.selector.render();

    const $dropdown = this.$el.find('.dropdown');

    $dropdown.on('show.bs.dropdown', () => this.selector.rerender());
  }

  generateCheckMap() {
    return _.chain(this.ko_model.selectedIds())
      .map(id => [id, {
        id,
        state: 'checked',
      }])
      .object()
      .value();
  }
}
